import type { IRemover } from '@/engine/UIPresentation/Interfaces';
import type { IBaseViewModel } from '@/engine/app/interfaces';
import { SceneItemKind } from '@/engine/app/interfaces';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ViewsService } from '@/engine/app/Views.service';
import type { ICommand } from '../../core/interfaces/Command';
import { BaseRemoveCommand } from './BaseRemove.command';
import type { IRemoveCommandsFactory } from '../IndicationRemoveHighlighter';
import { DistanceModel } from '../distance/Distance.model';
import { AngleModel } from '../angle/Angle.model';
import { AnnotationModel } from '../annotation/Annotation.model';
import { TYPES as INDTYPES } from '@/engine/app/modules/indications/iocTypes';
import { SplineIndicationRemoveCommand } from './SplineIndicationRemove.command';
import type { IIndicationVMAdder } from '../interfaces';
import { AreaIndicationRemoveCommand } from './AreaIndicationRemove.command';
import { TYPES } from '@/engine/ioc';
import { inject, injectable } from 'inversify';
import { AreaIndicationAdder } from '../area/AreaAdder';
import { BaseIndicationAdder } from '../distance/BaseIndicationAdder';
import { SplineIndicationAdder } from '../spline/SplineAdder';
import { CancelableRemoveCommand } from './CancelableRemove.command';
import type { CancellationController } from '@/engine/Controllers/Cancellation.controller';

@injectable()
export class RemoveIndicationCommandsFactory implements IRemoveCommandsFactory {
  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _viewsService: ViewsService;

  private readonly _indicationsVMAdder: IIndicationVMAdder;

  private readonly _remover: IRemover;

  private readonly _cancellationController: CancellationController;

  public constructor(
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(INDTYPES.IndicationsService) indicationsVMAdder: IIndicationVMAdder,
    @inject(TYPES.IRemover) remover: IRemover,
    @inject(TYPES.CancellationController) cancellationController: CancellationController
  ) {
    this._planningAlgorithm = planningAlgorithm;
    this._viewsService = viewsService;
    this._indicationsVMAdder = indicationsVMAdder;
    this._remover = remover;
    this._cancellationController = cancellationController;
  }

  public createCommand(kind: SceneItemKind, viewModel: IBaseViewModel): ICommand {
    switch (kind) {
      case SceneItemKind.IndicatedDistance:
        return new CancelableRemoveCommand(
          this._planningAlgorithm,
          this._remover,
          new BaseIndicationAdder(this._planningAlgorithm, this._indicationsVMAdder),
          viewModel,
          DistanceModel,
          this._cancellationController
        );
      case SceneItemKind.IndicatedAnnotation:
        return new BaseRemoveCommand(
          this._planningAlgorithm,
          this._remover,
          new BaseIndicationAdder(this._planningAlgorithm, this._indicationsVMAdder),
          viewModel,
          AnnotationModel
        );
      case SceneItemKind.IndicatedAngle:
        return new BaseRemoveCommand(
          this._planningAlgorithm,
          this._remover,
          new BaseIndicationAdder(this._planningAlgorithm, this._indicationsVMAdder),
          viewModel,
          AngleModel
        );
      case SceneItemKind.IndicatedAreaMeasurement:
        return new AreaIndicationRemoveCommand(
          this._planningAlgorithm,
          this._viewsService,
          this._remover,
          new AreaIndicationAdder(this._planningAlgorithm, this._indicationsVMAdder),
          viewModel
        );
      case SceneItemKind.IndicatedSplineMeasurement:
        return new SplineIndicationRemoveCommand(
          this._planningAlgorithm,
          this._remover,
          new SplineIndicationAdder(this._planningAlgorithm, this._indicationsVMAdder),
          viewModel
        );
      default:
        throw new Error('Invalid kind');
    }
  }
}
